import {
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  ErrorOutline as ErrorIcon,
  InfoOutlined as InfoIcon,
  CheckCircleOutline as SuccessIcon,
} from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGroups } from '../contexts/GroupContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { getGroup, isGroupMember, useInvite } from '../services/groupService';
import { IMG } from '../theme/assets';
import type { DesignTokens } from '../theme/designTokens';
import type { Group } from '../types';

// ----- 招待ページ presentational helpers (style-only / mock: design/final/invite.html) -----

const UNKNOWN_AVATAR = 'linear-gradient(135deg, #aeb6c6, #878f9f)';

/** カラー丸角アバター (グループ頭文字 or 不明時の「?」)。 */
const InviteAvatar: React.FC<{ background: string; children: React.ReactNode }> = ({
  background,
  children,
}) => (
  <Box
    sx={{
      width: 78,
      height: 78,
      borderRadius: '14px',
      display: 'grid',
      placeItems: 'center',
      color: '#fff',
      fontSize: 32,
      fontWeight: 900,
      m: '0 auto 18px',
      background,
      boxShadow: '0 10px 24px rgba(30,30,70,0.18)',
    }}
  >
    {children}
  </Box>
);

const ALERT_META = {
  info: { accent: '#5b56e0', lightText: '#3f4a6b', Icon: InfoIcon },
  success: { accent: '#16a34a', lightText: '#15803d', Icon: SuccessIcon },
  error: { accent: '#dc2626', lightText: '#b42318', Icon: ErrorIcon },
} as const;

/** info / success / error の淡色アラート (mock 準拠・ライト/ダーク両対応)。 */
const InviteAlert: React.FC<{
  type: keyof typeof ALERT_META;
  text: string;
  isDark: boolean;
}> = ({ type, text, isDark }) => {
  const meta = ALERT_META[type];
  const Icon = meta.Icon;
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '9px',
        alignItems: 'flex-start',
        textAlign: 'left',
        borderRadius: '12px',
        p: '12px 14px',
        fontSize: 12.5,
        lineHeight: 1.5,
        mb: '20px',
        background: isDark ? `${meta.accent}22` : `${meta.accent}14`,
        border: `1px solid ${meta.accent}${isDark ? '44' : '33'}`,
        color: isDark ? '#e8eaf2' : meta.lightText,
      }}
    >
      <Icon sx={{ fontSize: 18, color: meta.accent, flexShrink: 0, mt: '1px' }} />
      <Box component="span">{text}</Box>
    </Box>
  );
};

/** 全幅プライマリ / ghost ボタン (mock 準拠)。<button> なので onClick はそのまま保持。 */
const InviteButton: React.FC<{
  onClick: () => void;
  background?: string;
  ghost?: boolean;
  disabled?: boolean;
  isDark: boolean;
  tokens: DesignTokens;
  children: React.ReactNode;
}> = ({ onClick, background, ghost, disabled, isDark, tokens, children }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      width: '100%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '9px',
      border: ghost ? `1.5px solid ${tokens.line}` : 'none',
      borderRadius: '13px',
      p: '15px',
      fontFamily: 'inherit',
      fontSize: 15,
      fontWeight: 800,
      color: ghost ? tokens.ink : '#fff',
      cursor: disabled ? 'default' : 'pointer',
      background: ghost ? (isDark ? 'transparent' : '#fff') : background,
      boxShadow: ghost ? 'none' : '0 10px 24px rgba(40,50,90,0.2)',
      transition: '.15s',
      opacity: disabled ? 0.75 : 1,
      '&:hover': ghost
        ? { borderColor: tokens.teal, color: tokens.teal }
        : { filter: disabled ? 'none' : 'brightness(1.04)' },
      '& svg': { fontSize: 18 },
    }}
  >
    {children}
  </Box>
);

const InvitePage: React.FC = () => {
  const navigate = useNavigate();
  const { groupId, inviteId } = useParams<{ groupId: string; inviteId: string }>();
  const { user, loading: authLoading } = useAuth();
  const { refreshGroups } = useGroups();
  const { themeMode } = useThemeMode();
  const tokens = useThemeColors();
  const isDark = themeMode === 'dark';

  const [group, setGroup] = useState<Group | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadyMember, setAlreadyMember] = useState(false);

  const loadGroupInfo = useCallback(async () => {
    if (!groupId) {
      setError('無効な招待リンクです');
      setLoading(false);
      return;
    }

    try {
      const groupData = await getGroup(groupId);
      if (!groupData) {
        setError('グループが見つかりません');
        setLoading(false);
        return;
      }
      setGroup(groupData);

      // ユーザーがログイン済みの場合、既にメンバーかチェック
      if (user) {
        const isMember = await isGroupMember(groupId, user.uid);
        setAlreadyMember(isMember);
      }
    } catch (err) {
      console.error('Error loading group:', err);
      setError('グループ情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [groupId, user]);

  useEffect(() => {
    // 認証ロード中は待機
    if (authLoading) return;

    // 未ログインの場合はグループ情報を取得せず、ログインを促す
    if (!user) {
      setLoading(false);
      return;
    }

    // ログイン済みの場合のみグループ情報を取得
    if (groupId) {
      loadGroupInfo();
    }
  }, [authLoading, groupId, user, loadGroupInfo]);

  const handleJoinGroup = async () => {
    if (!user || !groupId || !inviteId) return;

    setJoining(true);
    setError(null);

    try {
      const result = await useInvite(groupId, inviteId, user.uid, {
        displayName: user.displayName || 'Unknown User',
        photoURL: user.photoURL || undefined,
      });

      if (result.success) {
        await refreshGroups();
        navigate(`/share?group=${groupId}`, {
          state: { message: `「${group?.name}」に参加しました！` },
        });
      } else {
        setError(result.error || '参加に失敗しました');
      }
    } catch (err) {
      console.error('Error joining group:', err);
      setError('グループへの参加に失敗しました');
    } finally {
      setJoining(false);
    }
  };

  const handleLogin = () => {
    // 現在のURLをstateとして渡してログイン後にリダイレクト
    navigate('/login', { state: { redirectTo: window.location.pathname } });
  };

  // ページ全体の背景 (mock body 準拠・ダークは沈んだ色に)
  const pageBg = isDark
    ? 'radial-gradient(900px 500px at 15% 0%, rgba(102,126,234,0.16), transparent 60%), radial-gradient(800px 500px at 100% 100%, rgba(13,148,136,0.14), transparent 55%), linear-gradient(135deg, #0a0f1c, #0b1322 55%, #070b14)'
    : 'radial-gradient(900px 500px at 15% 0%, rgba(102,126,234,0.18), transparent 60%), radial-gradient(800px 500px at 100% 100%, rgba(13,148,136,0.16), transparent 55%), linear-gradient(135deg, #f5f7fa, #e7ebf3 55%, #dfe4ee)';

  const pageSx = {
    minHeight: 'var(--app-vh)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 3,
    background: pageBg,
  } as const;

  // 認証ロード中 / グループ情報ロード中
  if (authLoading || (user && loading)) {
    return (
      <Box sx={pageSx}>
        <CircularProgress sx={{ color: tokens.teal }} />
      </Box>
    );
  }

  // ----- カードシェル (バナー + 本文) -----
  const renderCard = (body: React.ReactNode) => (
    <Box sx={pageSx}>
      <Box
        sx={{
          width: 'min(440px, 100%)',
          background: isDark ? 'rgba(20,26,42,0.92)' : 'rgba(255,255,255,0.98)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.7)'}`,
          borderRadius: '17px',
          boxShadow: '0 30px 70px rgba(40,40,90,0.22)',
          overflow: 'hidden',
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* バナー: owl + ロゴ */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            background: 'linear-gradient(135deg, rgba(102,126,234,0.92), rgba(139,92,246,0.82))',
            p: '18px',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background:
                'radial-gradient(380px 160px at 85% 130%, rgba(255,255,255,0.22), transparent 60%)',
            },
          }}
        >
          <Box
            component="img"
            src={IMG.owl.hero}
            alt=""
            sx={{
              width: 46,
              height: 46,
              objectFit: 'contain',
              position: 'relative',
              zIndex: 1,
              filter: 'drop-shadow(0 5px 10px rgba(20,20,50,0.28))',
            }}
          />
          <Box
            component="img"
            src="/mikiwame-logo.svg"
            alt="MIKIWAME"
            sx={{
              height: 42,
              position: 'relative',
              zIndex: 1,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
            }}
          />
        </Box>

        {/* 本文 */}
        <Box sx={{ p: '30px 30px 28px', textAlign: 'center' }}>{body}</Box>
      </Box>
    </Box>
  );

  const titleSx = { fontSize: 20, fontWeight: 900, m: 0, color: tokens.ink } as const;
  const descSx = {
    fontSize: 12.5,
    color: tokens.muted,
    m: '8px 0 0',
    lineHeight: 1.55,
  } as const;
  const msgSx = { fontSize: 14, fontWeight: 700, m: '16px 0 18px', color: tokens.ink } as const;

  // 未ログイン状態の表示
  if (!user) {
    return renderCard(
      <>
        <InviteAvatar background={UNKNOWN_AVATAR}>?</InviteAvatar>
        <Typography component="h1" sx={titleSx}>
          グループへの招待
        </Typography>
        <Typography sx={msgSx}>グループに招待されています</Typography>
        <InviteAlert type="info" text="グループに参加するにはログインが必要です" isDark={isDark} />
        <InviteButton onClick={handleLogin} background="#4285f4" isDark={isDark} tokens={tokens}>
          ログインして参加
        </InviteButton>
      </>,
    );
  }

  // エラー状態 (招待無効・グループ未検出など)
  if (error) {
    return renderCard(
      <>
        <InviteAvatar background={UNKNOWN_AVATAR}>?</InviteAvatar>
        <Typography component="h1" sx={titleSx}>
          招待を確認できません
        </Typography>
        <InviteAlert type="error" text={error} isDark={isDark} />
        <InviteButton onClick={() => navigate('/')} ghost isDark={isDark} tokens={tokens}>
          ホームに戻る
        </InviteButton>
      </>,
    );
  }

  // グループ情報あり
  if (group) {
    const letter = group.name.charAt(0).toUpperCase();
    return renderCard(
      <>
        <InviteAvatar background={group.color}>{letter}</InviteAvatar>
        <Typography component="h1" sx={titleSx}>
          {group.name}
        </Typography>
        {group.description && <Typography sx={descSx}>{group.description}</Typography>}

        {alreadyMember ? (
          <>
            <Box sx={{ mt: '16px' }} />
            <InviteAlert
              type="success"
              text="あなたは既にこのグループのメンバーです"
              isDark={isDark}
            />
            <InviteButton
              onClick={() => navigate('/share')}
              background={`linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`}
              isDark={isDark}
              tokens={tokens}
            >
              案件を見る
              <ArrowForwardIcon />
            </InviteButton>
          </>
        ) : (
          <>
            <Typography sx={msgSx}>このグループへの招待を受け取りました</Typography>
            <InviteButton
              onClick={handleJoinGroup}
              background={group.color}
              disabled={joining}
              isDark={isDark}
              tokens={tokens}
            >
              {joining ? (
                <>
                  <CircularProgress size={18} color="inherit" />
                  参加しています…
                </>
              ) : (
                <>
                  <CheckIcon />
                  グループに参加
                </>
              )}
            </InviteButton>
          </>
        )}
      </>,
    );
  }

  return renderCard(null);
};

export default InvitePage;
